import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TourService } from './tour.service';
import type { TourRect } from './tour.types';

const POPOVER_GAP = 12;
const POPOVER_WIDTH = 280;
const VIEWPORT_MARGIN = 12;

@Component({
  selector: 'tc-tour-overlay',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tour-overlay.component.html',
  styleUrl: './tour-overlay.component.css',
})
export class TourOverlayComponent implements OnInit {
  readonly tour = inject(TourService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly targetRect = signal<TourRect | null>(null);

  readonly spotlightTop = computed(() => {
    const rect = this.targetRect();
    if (!rect) return 0;
    return rect.top - this.tour.getSpotlightPadding();
  });

  readonly spotlightLeft = computed(() => {
    const rect = this.targetRect();
    if (!rect) return 0;
    return rect.left - this.tour.getSpotlightPadding();
  });

  readonly spotlightWidth = computed(() => {
    const rect = this.targetRect();
    if (!rect) return 0;
    return rect.width + this.tour.getSpotlightPadding() * 2;
  });

  readonly spotlightHeight = computed(() => {
    const rect = this.targetRect();
    if (!rect) return 0;
    return rect.height + this.tour.getSpotlightPadding() * 2;
  });

  readonly popoverTop = signal<number | null>(null);
  readonly popoverLeft = signal<number | null>(null);
  readonly popoverMaxWidth = signal(POPOVER_WIDTH);

  constructor() {
    effect(() => {
      const active = this.tour.active();
      const index = this.tour.currentIndex();
      const stepId = this.tour.currentStep()?.id ?? null;

      if (!active || !stepId) {
        untracked(() => {
          this.targetRect.set(null);
          this.popoverTop.set(null);
          this.popoverLeft.set(null);
        });
        return;
      }

      untracked(() => {
        void index;
        void stepId;
        this.updateTargetRect();
      });
    });
  }

  ngOnInit(): void {
    fromEvent(window, 'scroll', { capture: true })
      .pipe(debounceTime(16), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.tour.active()) this.updateTargetRect();
      });

    fromEvent(window, 'resize')
      .pipe(debounceTime(16), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.tour.active()) this.updateTargetRect();
      });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.tour.active()) this.tour.end();
  }

  skip(): void {
    this.tour.end(true);
  }

  onPrimaryAction(): void {
    if (this.tour.isLast()) {
      this.tour.end(true);
      return;
    }
    this.tour.next();
  }

  private updateTargetRect(): void {
    const el = this.tour.currentStep()?.element;
    if (!el) {
      this.targetRect.set(null);
      this.popoverTop.set(null);
      this.popoverLeft.set(null);
      return;
    }

    const rect = el.getBoundingClientRect();
    const next: TourRect = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
    this.targetRect.set(next);
    this.positionPopover(next);
  }

  private positionPopover(target: TourRect): void {
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const popoverW = Math.min(
      POPOVER_WIDTH,
      viewportW - VIEWPORT_MARGIN * 2,
    );
    this.popoverMaxWidth.set(popoverW);

    const pad = this.tour.getSpotlightPadding();
    const spotlightBottom = target.top + target.height + pad * 2;
    const spaceBelow = viewportH - spotlightBottom - POPOVER_GAP;
    const spaceAbove = target.top - pad - POPOVER_GAP;

    let top: number;
    if (spaceBelow >= 120 || spaceBelow >= spaceAbove) {
      top = spotlightBottom + POPOVER_GAP;
    } else {
      top = Math.max(VIEWPORT_MARGIN, target.top - pad - 160 - POPOVER_GAP);
    }

    let left = target.left + target.width / 2 - popoverW / 2;
    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, viewportW - popoverW - VIEWPORT_MARGIN),
    );

    this.popoverTop.set(top);
    this.popoverLeft.set(left);
  }
}
